import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Clock, ChevronRight, ShieldCheck, Wallet, LogIn } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import RechargeDialog from "@/components/RechargeDialog";
import { api, apiError } from "@/lib/api";
import { formatMac, isCompleteMac } from "@/lib/mac";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function ActivationWizard({ app, open, onOpenChange }) {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const isGuest = !user || user === false;
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ mac_app: "", key: "", email: "", contact: "" });
  const [idem, setIdem] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [rechargeOptions, setRechargeOptions] = useState({ balance_topups: [], credit_packages: [] });
  const [rechargePayload, setRechargePayload] = useState(null);
  const [rechargeOpen, setRechargeOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(1);
      setForm({ mac_app: "", key: "", email: "", contact: "" });
      setResult(null);
      setError("");
      setIdem((crypto.randomUUID && crypto.randomUUID()) || String(Date.now() + Math.random()));
    }
  }, [open, app]);

  const openRecharge = (p) => { setRechargePayload(p); setRechargeOpen(true); };
  const onRechargeSuccess = () => { refreshUser(); setStep(2); };

  if (!app) return null;

  // Activation with credits requires an account. Guests pay via Pix (order-based),
  // which is fulfilled automatically after the payment is confirmed.
  if (isGuest) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md" data-testid="activation-guest-gate">
          <DialogHeader>
            <DialogTitle className="font-heading">Entre para ativar com créditos</DialogTitle>
            <DialogDescription>
              Ativação com créditos é exclusiva de contas VULPIX. Sem conta, use o pagamento via Pix — a ativação
              dispara sozinha na confirmação.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-2">
            <Button className="w-full gap-2" onClick={() => navigate("/login")} data-testid="guest-gate-login-btn">
              <LogIn className="h-4 w-4" /> Entrar
            </Button>
            <Button variant="outline" className="w-full" onClick={() => navigate("/cadastro")} data-testid="guest-gate-register-btn">
              Criar conta grátis
            </Button>
            <Button variant="ghost" className="w-full" onClick={() => onOpenChange(false)} data-testid="guest-gate-close-btn">
              Voltar e pagar com Pix
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const macLabel = app.needs_device_id
    ? "ID do Dispositivo (Device ID)"
    : "Endereço MAC";
  const macPlaceholder = app.needs_device_id ? "Ex: 1234567890" : "Ex: 24:FC:E5:0B:03:36";

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onMacChange = (e) =>
    setForm((f) => ({ ...f, mac_app: app.needs_device_id ? e.target.value : formatMac(e.target.value) }));

  const validateStep1 = () => {
    if (!form.mac_app.trim()) return "Informe o " + macLabel + ".";
    if (!app.needs_device_id && !isCompleteMac(form.mac_app)) return "Informe um MAC válido (ex: 49:78:2d:63:87:d9).";
    if (app.needs_mac_key && !form.key.trim()) return "Informe a Chave (KEY).";
    return "";
  };

  const goConfirm = () => {
    const err = validateStep1();
    if (err) {
      setError(err);
      return;
    }
    setError("");
    setStep(2);
  };

  const submit = async (payWith = "credits") => {
    setStep(3);
    setError("");
    const payload = {
      app_id: app.id,
      mac_app: form.mac_app.trim(),
      key: form.key.trim() || null,
      idempotency_key: idem,
      pay_with: payWith,
    };
    try {
      const { data } = await api.post("/activations", payload);
      refreshUser();
      setResult(data);
      setStep(4);
    } catch (e) {
      const status = e?.response?.status;
      const detail = apiError(e);
      if (status === 402 || /insuficien/i.test(detail)) {
        try {
          const { data } = await api.get("/pix/recharge/options");
          setRechargeOptions(data);
        } catch { /* ignore */ }
        setError("");
        setResult(null);
        setStep(5);
        return;
      }
      setError(detail);
      setResult(null);
      setStep(4);
      toast.error(detail);
    }
  };

  const statusView = () => {
    const st = result?.status;
    if (st === "approved")
      return { icon: <CheckCircle2 className="h-12 w-12 text-green-500" />, title: "Ativação aprovada!", desc: "Seu aplicativo foi ativado com sucesso." };
    if (st === "pending")
      return { icon: <Clock className="h-12 w-12 text-amber-500" />, title: "Ativação em processamento", desc: "Estamos processando. Você será avisado assim que concluir." };
    return { icon: <XCircle className="h-12 w-12 text-destructive" />, title: "Não foi possível ativar", desc: error || result?.error_message || "Tente novamente ou fale com o suporte." };
  };

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="activation-wizard">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-3">
            {app.logo && (
              <img src={app.logo} alt="" className="h-8 w-8 rounded object-cover" onError={(e) => (e.target.style.display = "none")} />
            )}
            Ativar {app.name}
          </DialogTitle>
          <DialogDescription>Preencha os dados solicitados para ativar o aplicativo.</DialogDescription>
        </DialogHeader>

        {/* Stepper */}
        <div className="mb-2 flex items-center gap-2 text-xs text-muted-foreground">
          {["Dados", "Confirmar", "Processando", "Resultado"].map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <span className={`rounded-full px-2 py-0.5 ${step >= i + 1 ? "bg-primary text-primary-foreground" : "bg-muted"}`}>{i + 1}</span>
              <span className={step === i + 1 ? "text-foreground font-medium" : ""}>{s}</span>
              {i < 3 && <ChevronRight className="h-3 w-3" />}
            </div>
          ))}
        </div>

        {step === 1 && (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="mac">{macLabel}</Label>
              <Input id="mac" value={form.mac_app} onChange={onMacChange} maxLength={app.needs_device_id ? undefined : 17} placeholder={macPlaceholder} data-testid="activation-mac-input" />
            </div>
            {app.needs_mac_key && (
              <div className="space-y-2">
                <Label htmlFor="key">Chave (KEY)</Label>
                <Input id="key" value={form.key} onChange={set("key")} placeholder="Chave do aplicativo" data-testid="activation-key-input" />
              </div>
            )}
            {error && <p className="text-sm text-destructive" data-testid="activation-error">{error}</p>}
            <Button className="w-full" onClick={goConfirm} data-testid="activation-continue-btn">
              Continuar
            </Button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4">
            <div className="rounded-lg border border-border p-4">
              <Row label="Aplicativo" value={app.name} />
              <Row label={macLabel} value={form.mac_app} />
              {app.needs_mac_key && <Row label="Chave" value={form.key} />}
              {!isGuest && <Row label="Custo em créditos" value={`${app.credit_cost} crédito(s)`} />}
              {!isGuest && Number(app.price || 0) > 0 && <Row label="Preço em saldo" value={`R$ ${Number(app.price).toFixed(2)}`} />}
            </div>
            {!isGuest && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="h-4 w-4 text-primary" /> Você só é debitado se a ativação for concluída. Em caso de falha, o valor é estornado.
              </div>
            )}
            <div className="space-y-2">
              <Button className="w-full" onClick={() => submit("credits")} data-testid="activation-confirm-btn">
                Pagar com créditos ({app.credit_cost})
              </Button>
              {Number(user?.balance || 0) >= Number(app.price || 0) && Number(app.price || 0) > 0 && (
                <Button variant="secondary" className="w-full gap-2" onClick={() => submit("balance")} data-testid="activation-pay-balance-btn">
                  <Wallet className="h-4 w-4" /> Pagar com saldo (R$ {Number(app.price).toFixed(2)})
                </Button>
              )}
              <Button variant="ghost" className="w-full" onClick={() => setStep(1)} data-testid="activation-back-btn">Voltar</Button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="flex flex-col items-center justify-center gap-4 py-10">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Processando sua ativação...</p>
          </div>
        )}

        {step === 4 && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center gap-4 py-6 text-center" data-testid="activation-result">
            {statusView().icon}
            <div>
              <h3 className="font-heading text-lg font-semibold">{statusView().title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{statusView().desc}</p>
            </div>
            {result?.status === "approved" && (
              <Badge variant="secondary" data-testid="activation-status-badge">Protocolo: {result.id?.slice(0, 8)}</Badge>
            )}
            <Button className="w-full" onClick={() => onOpenChange(false)} data-testid="activation-close-btn">Fechar</Button>
          </motion.div>
        )}

        {step === 5 && (
          <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-5" data-testid="activation-insufficient">
            <div className="flex flex-col items-center gap-2 text-center">
              <Wallet className="h-11 w-11 text-primary" />
              <h3 className="font-heading text-lg font-semibold">Créditos insuficientes</h3>
              <p className="text-sm text-muted-foreground">
                Este app custa <b>{app.credit_cost} crédito(s)</b> e você tem <b>{Number(user?.credits || 0)}</b>.
                Recarregue abaixo para concluir a ativação.
              </p>
            </div>

            {Number(user?.balance || 0) >= Number(app.price || 0) && Number(app.price || 0) > 0 && (
              <Button className="w-full gap-2" onClick={() => submit("balance")} data-testid="insuf-pay-balance-btn">
                <Wallet className="h-4 w-4" /> Pagar agora com saldo (R$ {Number(app.price).toFixed(2)})
              </Button>
            )}

            {rechargeOptions.credit_packages?.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Pacotes de créditos</p>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {rechargeOptions.credit_packages.map((p) => (
                    <Button key={p.id} variant="outline" className="h-auto justify-between py-2.5 hover:border-primary"
                      data-testid={`insuf-package-${p.id}`}
                      onClick={() => openRecharge({ kind: "credits", package_id: p.id, label: `Comprar ${p.credits} créditos` })}>
                      <span className="font-medium">{p.credits} créditos</span>
                      <span className="font-num text-primary">R$ {Number(p.price).toFixed(2)}</span>
                    </Button>
                  ))}
                </div>
              </div>
            )}

            {rechargeOptions.balance_topups?.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Adicionar saldo (R$)</p>
                <div className="grid grid-cols-4 gap-2">
                  {rechargeOptions.balance_topups.slice(0, 8).map((v) => (
                    <Button key={v} variant="outline" size="sm" className="font-num hover:border-primary"
                      data-testid={`insuf-topup-${v}`}
                      onClick={() => openRecharge({ kind: "balance", amount: v, label: `Adicionar R$ ${v} de saldo` })}>
                      {v}
                    </Button>
                  ))}
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-1">
              <Button variant="ghost" className="flex-1" onClick={() => onOpenChange(false)} data-testid="insuf-close-btn">Fechar</Button>
              <Button className="flex-1" onClick={() => setStep(2)} data-testid="insuf-retry-btn">Já recarreguei, tentar</Button>
            </div>
          </motion.div>
        )}
      </DialogContent>
    </Dialog>
    <RechargeDialog open={rechargeOpen} onOpenChange={setRechargeOpen} payload={rechargePayload} onSuccess={onRechargeSuccess} />
    </>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between border-b border-border py-2 text-sm last:border-0">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium">{value}</span>
    </div>
  );
}
